import { DYE_SOURCE_TYPES } from '@dyeatlas/shared'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field, Input } from '@/components/ui/Field'
import { Select, toOptions } from '@/components/ui/Select'
import { api, ApiError } from '@/lib/api'

export interface MasterEntry {
  id: string
  code: string
  name: string
  status: 'active' | 'inactive'
  linkedRecords: number
  updatedAt: string
  scientificName?: string
  sourceType?: string | null
  regionOfOrigin?: string | null
  typicalHue?: string | null
  swatchHex?: string | null
  year?: number | null
  holdingInstitution?: string | null
  attrs?: Record<string, string>
}

interface Draft {
  name: string
  scientificName: string
  sourceType: string
  regionOfOrigin: string
  typicalHue: string
  swatchHex: string
  year: string
  holdingInstitution: string
}

const EMPTY: Draft = {
  name: '',
  scientificName: '',
  sourceType: '',
  regionOfOrigin: '',
  typicalHue: '',
  swatchHex: '',
  year: '',
  holdingInstitution: '',
}

/**
 * One dialog for all master-data kinds. Dye sources and manuscripts carry extra
 * fields; every other vocabulary is just a canonical name, so the form collapses
 * to a single input rather than showing irrelevant blanks.
 */
export function EntryDialog({
  kind,
  label,
  entry,
  open,
  onOpenChange,
  onSaved,
}: {
  kind: string
  label: string
  entry: MasterEntry | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void | Promise<unknown>
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setDraft(
      entry
        ? {
            name: entry.name,
            scientificName: entry.scientificName ?? '',
            sourceType: entry.sourceType ?? '',
            regionOfOrigin: entry.regionOfOrigin ?? '',
            typicalHue: entry.typicalHue ?? '',
            swatchHex: entry.swatchHex ?? '',
            year: entry.year ? String(entry.year) : '',
            holdingInstitution: entry.holdingInstitution ?? '',
          }
        : EMPTY,
    )
  }, [open, entry])

  const isDyeSource = kind === 'dyeSource'
  const isManuscript = kind === 'manuscript'

  const save = useMutation({
    mutationFn: () => {
      const body = isDyeSource
        ? {
            canonicalName: draft.name,
            scientificName: draft.scientificName,
            sourceType: draft.sourceType || undefined,
            regionOfOrigin: draft.regionOfOrigin,
            typicalHue: draft.typicalHue,
            swatchHex: draft.swatchHex || undefined,
          }
        : isManuscript
          ? {
              name: draft.name,
              year: draft.year,
              holdingInstitution: draft.holdingInstitution,
            }
          : { name: draft.name }

      return entry
        ? api.patch(`/admin/master-data/${kind}/${entry.id}`, body)
        : api.post(`/admin/master-data/${kind}`, body)
    },
    onSuccess: async () => {
      await onSaved()
      onOpenChange(false)
      toast.success(entry ? 'Entry updated.' : 'Entry added.')
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : 'Could not save that entry.')
    },
  })

  const singular = label.replace(/s$/, '').toLowerCase()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={entry ? `Edit ${singular} entry` : `Add ${singular} entry`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!draft.name.trim()}>
            Save entry
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-[var(--radius-control)] border border-brand-line bg-brand-soft px-3 py-2 text-[13px] text-brand">
            {error}
          </p>
        )}

        <Field label={isDyeSource ? 'Common name' : 'Name'} htmlFor="name" required>
          <Input
            id="name"
            autoFocus
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            placeholder={isDyeSource ? 'Madder' : `New ${singular}`}
          />
        </Field>

        {isDyeSource && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Scientific name" htmlFor="scientificName" required>
                <Input
                  id="scientificName"
                  value={draft.scientificName}
                  onChange={(event) => setDraft({ ...draft, scientificName: event.target.value })}
                  placeholder="Rubia tinctorum"
                />
              </Field>
              <Field label="Source type">
                <Select
                  value={draft.sourceType || null}
                  onChange={(value) => setDraft({ ...draft, sourceType: value })}
                  options={toOptions(DYE_SOURCE_TYPES)}
                />
              </Field>
            </div>

            <Field label="Region of origin" htmlFor="regionOfOrigin">
              <Input
                id="regionOfOrigin"
                value={draft.regionOfOrigin}
                onChange={(event) => setDraft({ ...draft, regionOfOrigin: event.target.value })}
                placeholder="Mediterranean"
              />
            </Field>

            <div className="grid grid-cols-[1fr_auto] gap-3">
              <Field label="Typical hue" htmlFor="typicalHue">
                <Input
                  id="typicalHue"
                  value={draft.typicalHue}
                  onChange={(event) => setDraft({ ...draft, typicalHue: event.target.value })}
                  placeholder="Red / pink"
                />
              </Field>
              <Field label="Swatch">
                <input
                  type="color"
                  value={draft.swatchHex || '#9b2c21'}
                  onChange={(event) => setDraft({ ...draft, swatchHex: event.target.value })}
                  className="h-10 w-16 cursor-pointer rounded-[var(--radius-control)] border border-line bg-paper p-1"
                />
              </Field>
            </div>
          </>
        )}

        {isManuscript && (
          <div className="grid grid-cols-[100px_1fr] gap-3">
            <Field label="Year" htmlFor="year">
              <Input
                id="year"
                inputMode="numeric"
                value={draft.year}
                onChange={(event) => setDraft({ ...draft, year: event.target.value })}
                placeholder="1778"
              />
            </Field>
            <Field label="Holding institution" htmlFor="holdingInstitution">
              <Input
                id="holdingInstitution"
                value={draft.holdingInstitution}
                onChange={(event) => setDraft({ ...draft, holdingInstitution: event.target.value })}
              />
            </Field>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-line pt-3 text-[12px] text-ink-muted">
          Status on save
          <span className="inline-flex items-center gap-1.5 text-positive">
            <span className="size-1.5 rounded-full bg-positive" />
            {entry?.status === 'inactive' ? 'Inactive' : 'Active'}
          </span>
        </div>
      </div>
    </Dialog>
  )
}
